import { type LatLngBounds } from 'leaflet';
import { redirectToSerpPage } from '@pkgs/components/utils/redirectToSerpPage';
import { latLngBoundsToMultiPolygon } from '@pkgs/components/utils/map';
import { MAXIMUM_MAP_BBOX } from '@pkgs/components/consts/map';
import { convertBoundingBoxDtoToMultiPolygon, type MultiPolygonG2D, type BoundingBoxDto } from '@ev-tech/search-modules-api';

import { MapMode } from '../hooks/stores/search/slices/mapInteractionSlice';
import { type SearchState } from '../hooks/stores/search/SearchStoreProvider';

type UpdateMapViewToGlobalParams = {
  placeDetailsBoundingBox?: BoundingBoxDto;
  placeIds?: string[];
  resetShopState: () => void;
  setPage: (page: number) => void;
  setSearchRadius: (searchRadius?: number) => void;
  trackSearchThisAreaClick: (multiPolygon: MultiPolygonG2D) => void;
  updateMapBounds: (bounds: BoundingBoxDto) => void;
};

type SearchGlobalButtonClickParams = {
  searchState: SearchState;
  isLipaPage: boolean;
  isPrivateOffice?: boolean;
  options: Parameters<typeof redirectToSerpPage>[1];
  combinedBoundingBox?: BoundingBoxDto;
  resetShopState: () => void;
  setMasterDataShopIds: (masterDataShopIds?: string[]) => void;
  setSearchRadius: (searchRadius?: number) => void;
  setShopIds: (shopIds?: string[]) => void;
  trackSearchGlobalNetworkButtonClick: () => void;
  updateMapBounds: (bounds: BoundingBoxDto) => void;
};

type UpdatePropertiesForMapViewParams = {
  mapBounds: LatLngBounds;
  setAutoCompleteOptionCategory: (category: string) => void;
  setAutoCompleteOptionIndex: (index?: number) => void;
  setMultiPolygon: (multiPolygon: MultiPolygonG2D, options?: { mapMode?: MapMode }) => void;
  setInputText: (inputText?: string) => void;
  setNumberOfAutoCompleteOptions: (numberOfAutoCompleteOptions?: number) => void;
  setPage: (page: number) => void;
  setTypedInText: (typedInText?: string) => void;
  trackSearchThisAreaClick?: (multiPolygon: MultiPolygonG2D) => void;
};

export const handleSearchGlobalButtonClick = ({
  searchState,
  isLipaPage,
  isPrivateOffice,
  options,
  combinedBoundingBox,
  resetShopState,
  setMasterDataShopIds,
  setSearchRadius,
  setShopIds,
  trackSearchGlobalNetworkButtonClick,
  updateMapBounds,
}: SearchGlobalButtonClickParams) => {
  setSearchRadius(0);
  if (isPrivateOffice) {
    redirectToSerpPage(searchState, options);
  } else if (isLipaPage) {
    trackSearchGlobalNetworkButtonClick();
    resetShopState();
    updateMapBounds(combinedBoundingBox ?? MAXIMUM_MAP_BBOX);
  } else {
    setShopIds(undefined);
    setMasterDataShopIds(undefined);
  }
};

export const updateMapViewToGlobal = ({
  placeDetailsBoundingBox,
  placeIds,
  resetShopState,
  setPage,
  setSearchRadius,
  trackSearchThisAreaClick,
  updateMapBounds,
}: UpdateMapViewToGlobalParams) => {
  setSearchRadius(0);
  trackSearchThisAreaClick(convertBoundingBoxDtoToMultiPolygon(MAXIMUM_MAP_BBOX));
  resetShopState();
  updateMapBounds(placeIds?.length && placeDetailsBoundingBox ? placeDetailsBoundingBox : MAXIMUM_MAP_BBOX);
  setPage(1);
};

export const updatePropertiesForMapView = ({
  mapBounds,
  setAutoCompleteOptionCategory,
  setAutoCompleteOptionIndex,
  setMultiPolygon,
  setInputText,
  setNumberOfAutoCompleteOptions,
  setPage,
  setTypedInText,
  trackSearchThisAreaClick,
}: UpdatePropertiesForMapViewParams) => {
  const multiPolygon = latLngBoundsToMultiPolygon(mapBounds);
  setMultiPolygon(multiPolygon, { mapMode: MapMode.BOUNDING_BOX_MODE });
  setPage(1);
  setTypedInText(undefined);
  setInputText(undefined);
  setNumberOfAutoCompleteOptions(undefined);
  setAutoCompleteOptionIndex(undefined);
  setAutoCompleteOptionCategory('map-search');
  trackSearchThisAreaClick?.(multiPolygon);
};
